import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View, RefreshControl } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import CutCard from '@/components/General/CutCard';
import CaregiverRow from '@/components/FollowUp/CaregiverRow';
import MonitoringModeRow from '@/components/FollowUp/MonitoringModeRow';
import ContactWarningBanner from '@/components/FollowUp/ContactWarningBanner';
import AttentionReasonsCard from '@/components/FollowUp/AttentionReasonsCard';
import TouchSummaryCards from '@/components/FollowUp/TouchSummaryCards';
import TouchTimeline from '@/components/FollowUp/TouchTimeline';
import FollowUpPersonTasks from '@/components/FollowUp/FollowUpPersonTasks';
import LogTouchSheet from '@/components/FollowUp/LogTouchSheet';
import CreateFollowUpTaskSheet from '@/components/FollowUp/CreateFollowUpTaskSheet';
import StartPathSheet from '@/components/FollowUp/Paths/StartPathSheet';
import StartCareSheet from '@/components/FollowUp/Care/StartCareSheet';
import PersonEnrollmentsCard from '@/components/FollowUp/Paths/PersonEnrollmentsCard';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { extractFollowUpCode } from '@/data/followUp';

/**
 * FollowUpCareSurface — the person care surface rendered by the People
 * profile Follow Up tab (the legacy /follow-up/people/[personID] route
 * redirects there).
 * Server-confirmed state only — nothing is optimistically granted/shown.
 */
const FollowUpCareSurface = ({ personID }) => {
    const queryClient = useQueryClient();
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;

    const { data: activeCareEpisode } = Query.followUp.personCareEpisode({
        organizationID,
        personID,
        options: { enabled: !!organizationID && !!personID && !!loggedInUser?.can?.('View Follow Up') },
    });

    const { data: person, isLoading, isError, error, isRefetching } = Query.followUp.person({
        organizationID,
        personID,
        options: { enabled: !!organizationID && !!personID && !!loggedInUser?.can?.('View Follow Up') },
    });
    const { data: profile } = Query.people.basicById({ id: personID });
    const { data: notes = [] } = Query.followUp.personNotes({
        organizationID,
        personID,
        options: { enabled: !!organizationID && !!personID && !!loggedInUser?.can?.('View Follow Up') },
    });

    // Team notes ride the touch timeline: notes linked to a touch render
    // inside that touch's card; standalone FOLLOW_UP-visibility notes (no
    // followUpTouchID) stay visible under an "Other notes" subsection.
    const { notesByTouchID, standaloneNotes } = useMemo(() => {
        const byTouch = {};
        const standalone = [];
        for (const note of notes) {
            if (note.followUpTouchID) {
                (byTouch[note.followUpTouchID] ||= []).push(note);
            } else {
                standalone.push(note);
            }
        }
        return { notesByTouchID: byTouch, standaloneNotes: standalone };
    }, [notes]);

    const canManage = loggedInUser?.can?.('Manage Follow Up');
    const personName = profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : null;
    const isPrimaryCaregiver = !!person?.primaryCaregiverPersonID && person.primaryCaregiverPersonID === loggedInUser?.id;

    const [logOpen, setLogOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [pathOpen, setPathOpen] = useState(false);
    const [careOpen, setCareOpen] = useState(false);
    const [correctingTouch, setCorrectingTouch] = useState(null);

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(organizationID, personID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personNotes(organizationID, personID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personTasks(organizationID, personID) });
        queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'enrollments'] });
        queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'careEpisodes'] });
    };

    if (isError) {
        const code = extractFollowUpCode(error) || error?.followUpCode;
        return (
            <EmptyListPlaceholder
                type="404"
                spacing={60}
                message={code === 'FOLLOW_UP_NOT_FOUND'
                    ? "We couldn't find that person"
                    : code === 'FOLLOW_UP_DENIED'
                        ? "You don't have permission to view Follow Up"
                        : 'Something went wrong loading Follow Up'}
            />
        );
    }

    if (isLoading || !person) {
        return (
            <View style={styles.loadingWrap}>
                <ActivityIndicator size="large" />
            </View>
        );
    }

    return (
        <>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refresh} />}
            >
                <ContactWarningBanner
                    organizationID={organizationID}
                    personID={personID}
                    contactWarning={person.contactWarning}
                    canManage={canManage}
                />

                <CutCard accentColor="paths" style={styles.actionCard}>
                    <Text style={styles.heading}>Stay connected</Text>
                    <Text style={styles.hint}>Record a conversation or plan your team&rsquo;s next step.</Text>
                    <View style={styles.actionsRow}>
                        <SolidButton testID="follow-up-log-touch-button" size="sm" type="paths" iconName="add" text="Log Touch" onPress={() => setLogOpen(true)} />
                        <SolidButton testID="follow-up-create-task-button" size="sm" iconName="clipboard-outline" variant="outlined" type="paths" text="Create Task" onPress={() => setCreateOpen(true)} />
                        <SolidButton testID="follow-up-start-care-button" size="sm" iconName="heart-outline" variant="outlined" type="paths" text="Start Care" onPress={() => setCareOpen(true)} />
                        <SolidButton testID="follow-up-start-path-button" size="sm" iconName="git-branch-outline" variant="outlined" type="paths" text="Start Path" onPress={() => setPathOpen(true)} />
                    </View>
                </CutCard>
                <TouchSummaryCards person={person} />

                <View style={styles.duoRow}>
                <CutCard accentColor="paths" style={styles.duoCard}>
                    <CaregiverRow
                        organizationID={organizationID}
                        personID={personID}
                        caregiverPersonID={person.primaryCaregiverPersonID}
                        canManage={canManage}
                    />
                </CutCard>
                <CutCard accentColor="paths" style={styles.duoCard}>
                    <MonitoringModeRow
                        organizationID={organizationID}
                        personID={personID}
                        monitoringMode={person.monitoringMode}
                        canManage={canManage}
                    />
                </CutCard>
                </View>

                {/* Active attention reasons (P5) — per-reason rows, each
                    snoozable independently by a manager so an attendance
                    snooze never erases contact flags (spec §11.6). */}
                <AttentionReasonsCard
                    organizationID={organizationID}
                    personID={personID}
                    reasons={person.attentionReasons || person.attention || []}
                    canManage={canManage}
                />

                {/* Active care episode (§25.9) — template-free; links to the
                    episode route. Repeat Start Care reopens this episode. */}
                {!!activeCareEpisode && (
                    <Pressable
                        testID={`person-care-episode-${activeCareEpisode.id}`}
                        accessibilityRole="button"
                        onPress={() => router.push(`/follow-up/care/${activeCareEpisode.id}`)}
                        style={styles.careRow}
                    >
                        <View style={styles.careIcon}>
                            <Unicon name="heart-outline" size={15} colorVariant="paths" />
                        </View>
                        <View style={styles.careBody}>
                            <Text style={styles.careTitle} numberOfLines={1}>{activeCareEpisode.context || 'Active care'}</Text>
                            <Text style={styles.careMeta} numberOfLines={1}>Care is active — open the episode</Text>
                        </View>
                        <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
                    </Pressable>
                )}

                {/* Active + past Path enrollments (spec §6.2) — each links to
                    the enrollment progress detail. */}
                <PersonEnrollmentsCard organizationID={organizationID} personID={personID} />

                <FollowUpPersonTasks
                    organizationID={organizationID}
                    personID={personID}
                    canManage={canManage}
                    isPrimaryCaregiver={isPrimaryCaregiver}
                />

                <Text style={styles.sectionTitle}>Touches</Text>
                <TouchTimeline
                    touches={person.touches}
                    notesByTouchID={notesByTouchID}
                    standaloneNotes={standaloneNotes}
                    loggedInUser={loggedInUser}
                    organizationID={organizationID}
                    personID={personID}
                    onCorrect={setCorrectingTouch}
                />
            </ScrollView>

            <LogTouchSheet
                isOpen={logOpen}
                onClose={() => setLogOpen(false)}
                organizationID={organizationID}
                personID={personID}
                personName={personName}
            />
            <LogTouchSheet
                isOpen={!!correctingTouch}
                onClose={() => setCorrectingTouch(null)}
                organizationID={organizationID}
                personID={personID}
                personName={personName}
                touch={correctingTouch}
            />
            <CreateFollowUpTaskSheet
                isOpen={createOpen}
                onClose={() => setCreateOpen(false)}
                organizationID={organizationID}
                personID={personID}
                personName={personName}
                primaryCaregiverPersonID={person.primaryCaregiverPersonID}
            />
            <StartPathSheet
                isOpen={pathOpen}
                onClose={() => setPathOpen(false)}
                organizationID={organizationID}
                personID={personID}
                personName={personName}
            />
            <StartCareSheet
                isOpen={careOpen}
                onClose={() => setCareOpen(false)}
                organizationID={organizationID}
                personID={personID}
                personName={personName}
            />
        </>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    scroll: { flex: 1 },
    content: { padding: 16, paddingBottom: 60, gap: 16, maxWidth: 1180, width: '100%', alignSelf: 'center' },
    loadingWrap: { flex: 1, padding: 40, alignItems: 'center', justifyContent: 'center' },
    card: {
        padding: 14,
        gap: 12,
        boxShadow: shadows.card,
    },
    careRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.pathsContainer,
        borderRadius: 12,
        padding: 12,
        boxShadow: shadows.card,
        _web: { cursor: 'pointer' },
    },
    careIcon: {
        width: 30,
        height: 30,
        borderRadius: 8,
        backgroundColor: colors.surface,
        alignItems: 'center',
        justifyContent: 'center',
    },
    careBody: { flex: 1, minWidth: 0 },
    careTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    careMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
    duoRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
    duoCard: { flex: 1, minWidth: 260, padding: 14, boxShadow: shadows.card },
    actionCard: { backgroundColor: colors.pathsContainer, padding: 16, gap: 8, borderRadius: 3 },
    heading: { fontSize: 18, fontFamily: fonts.heading, color: colors.text },
    hint: { fontSize: 13, color: colors.textSecondary, lineHeight: 19 },
    actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    sectionTitle: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 4,
    },
}));

export default FollowUpCareSurface;
